import { useRef } from 'react';
import { PlusIcon, XIcon } from 'lucide-react';
import { useController, useFormContext } from 'react-hook-form';
import { partitionBySize } from '@noor/lib';
import { cn } from '@noor/lib/cn';
import type { ProductFormImage } from '../model/schema';

/**
 * The mobile photo picker's cap (Figma 26609:14325 — "Максимум 7 фото").
 *
 * Distinct from the desktop `FormField.ImageGrid`'s cap of 5: that component
 * draws a fixed cover-plus-2x2 layout with its own promote-to-cover star,
 * where 5 is a property of the LAYOUT. This mobile design draws a plain
 * flowing tile grid with no star affordance at all — the first photo is the
 * cover implicitly — so it has no layout reason to share that number.
 */
const MAX_PHOTOS = 7;

interface MobilePhotoGridProps {
  name: string;
  uploadLabel: string;
  hintLabel: string;
  removeLabel: string;
  onRejected?: (names: string[]) => void;
  className?: string;
}

/**
 * Mobile product photo picker (Figma 26609:14325): a single gray card holding
 * a "Загрузить фото" label, a max-7 hint, and a flowing grid of square tiles
 * — a white "+" tile followed by whatever has been picked.
 *
 * No promote-to-cover star: the design shows none, and the first entry is
 * simply the cover by position (the same rule the wire's `imageKeys` order
 * already encodes — see `useUploadProductImages`). Only a remove-X per tile.
 */
export function MobilePhotoGrid({
  name,
  uploadLabel,
  hintLabel,
  removeLabel,
  onRejected,
  className,
}: MobilePhotoGridProps) {
  const { control } = useFormContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const { field } = useController({ control, name });
  const images: ProductFormImage[] =
    (field.value as ProductFormImage[] | undefined) ?? [];
  const onChange = field.onChange as (value: ProductFormImage[]) => void;

  function handlePick(files: FileList | null): void {
    if (!files || files.length === 0) return;
    const room = MAX_PHOTOS - images.length;
    const picked = Array.from(files).slice(0, Math.max(room, 0));
    const { accepted, rejected } = partitionBySize(picked);
    if (rejected.length > 0) onRejected?.(rejected);

    const next: ProductFormImage[] = accepted.map((file) => ({
      id: crypto.randomUUID(),
      url: URL.createObjectURL(file),
      file,
      isPrimary: images.length === 0,
    }));
    if (next.length > 0) onChange([...images, ...next]);
    if (inputRef.current) inputRef.current.value = '';
  }

  function handleRemove(id: string): void {
    const removed = images.find((image) => image.id === id);
    if (removed?.file) URL.revokeObjectURL(removed.url);
    const rest = images.filter((image) => image.id !== id);
    // The cover is positional, so removing the first tile hands the crown to
    // whichever photo is now first rather than leaving no cover at all.
    onChange(rest.map((image, index) => ({ ...image, isPrimary: index === 0 })));
  }

  return (
    <div className={cn('flex flex-col gap-4 rounded-[20px] bg-muted p-4', className)}>
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium tracking-tight text-foreground">
          {uploadLabel}
        </span>
        <span className="text-xs tracking-tight text-muted-foreground">{hintLabel}</span>
      </div>

      <div className="flex flex-wrap gap-3">
        {images.length < MAX_PHOTOS && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            aria-label={uploadLabel}
            className="flex size-[72px] shrink-0 cursor-pointer items-center justify-center rounded-2xl bg-card text-foreground transition-colors outline-none hover:bg-card/70 focus-visible:ring-[3px] focus-visible:ring-ring/40"
          >
            <PlusIcon className="size-8" aria-hidden />
          </button>
        )}

        {images.map((image) => (
          <div
            key={image.id}
            className="relative size-[72px] shrink-0 overflow-hidden rounded-2xl bg-card"
          >
            <img
              src={image.url}
              alt=""
              className="size-full object-cover"
              draggable={false}
            />
            <button
              type="button"
              onClick={() => handleRemove(image.id)}
              aria-label={removeLabel}
              className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-full bg-foreground/70 text-background outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
            >
              <XIcon className="size-3" aria-hidden />
            </button>
          </div>
        ))}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handlePick(e.target.files)}
      />
    </div>
  );
}
